import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  Group,
  LoadingOverlay,
  NumberInput,
  ScrollArea,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons";
import { CREATE_LOAN_PRODUCT } from "apollo/mutuations";
import {
  ADMIN_LOAN_CATEGORIES,
  ADMIN_LOAN_PRODUCTS,
  AUTH,
  LOAN_PROVIDERS,
} from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

const LoanProductAddModal = ({ setOpened, size, activePage, refetch }) => {
  const [isLoanProvider, setIsLoanProvider] = useState(false);
  const [currentProviderId, setCurrentProviderId] = useState(null);

  // Check if current user is a loan provider
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles")) || [];
    const isProvider = roles.includes("loan_provider");
    setIsLoanProvider(isProvider);
  }, []);

  // Get current user data
  const { data: authData } = useQuery(AUTH, {
    skip: !isLoanProvider,
  });
  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      interest_type: "FLAT",
      interest_rate: 0,
      repayment_frequency: "MONTHLY",
      min_amount: 0,
      max_amount: 0,
      min_term: 1,
      max_term: 12,
      processing_fee_type: "PERCENT",
      processing_fee: 0,
      penalty_rate: 0,
      grace_period_days: 0,
      category_id: "",
      provider_id: "",
      allowed_terms: "",
    },
    validate: {
      name: (value) => (value ? null : "Name is required"),
      interest_rate: (value) =>
        value >= 0 ? null : "Interest rate must be 0 or greater",
      min_amount: (value) =>
        value >= 0 ? null : "Minimum amount must be 0 or greater",
      max_amount: (value, values) =>
        value >= values.min_amount
          ? null
          : "Maximum amount must be >= minimum amount",
      processing_fee: (value) =>
        value >= 0 ? null : "Processing fee must be 0 or greater",
    },
  });

  // Fetch loan providers
  const { data: providersData, refetch: refetchProviders } = useQuery(
    LOAN_PROVIDERS,
    {
      variables: {
        first: 1000,
        page: 1,
        ordered_by: [{ column: "NAME", order: "ASC" }],
      },
      onCompleted(data) {
        // Match provider by user_id when available
        if (isLoanProvider && authData?.auth?.id) {
          const provider = data?.loanProviders?.data?.find(
            (p) => p.user_id === authData.auth.id,
          );
          if (provider) {
            setCurrentProviderId(provider.id);
            form.setFieldValue("provider_id", provider.id);
          }
        }
      },
    },
  );

  // Fetch loan categories
  const { data: categoriesData, refetch: refetchCategories } = useQuery(
    ADMIN_LOAN_CATEGORIES,
    {
      variables: {
        first: 1000,
        page: 1,
        orderBy: [{ column: "NAME", order: "ASC" }],
      },
    },
  );
  useEffect(() => {
    if (!isLoanProvider) {
      refetchProviders();
    }
    refetchCategories();
  }, [isLoanProvider]);

  const [createLoanProduct, { loading }] = useMutation(CREATE_LOAN_PRODUCT, {
    refetchQueries: [
      {
        query: ADMIN_LOAN_PRODUCTS,
        variables: {
          first: parseInt(size),
          page: activePage,
          search: "",
          orderBy: [
            {
              column: "NAME",
              order: "ASC",
            },
          ],
        },
      },
    ],
    onCompleted() {
      showNotification({
        color: "green",
        title: "Success",
        message: "Loan Product Created Successfully",
      });
      setOpened(false);
      form.reset();
      if (refetch) refetch();
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Create Loan Product",
      });
    },
  });

  const handleSubmit = (values) => {
    createLoanProduct({
      variables: {
        name: values.name,
        description: values.description || null,
        interest_type: values.interest_type,
        interest_rate: parseFloat(values.interest_rate),
        repayment_frequency: values.repayment_frequency,
        min_amount: parseFloat(values.min_amount),
        max_amount: parseFloat(values.max_amount),
        min_term: parseInt(values.min_term),
        max_term: parseInt(values.max_term),
        processing_fee_type: values.processing_fee_type,
        processing_fee: parseFloat(values.processing_fee),
        penalty_rate: values.penalty_rate
          ? parseFloat(values.penalty_rate)
          : null,
        grace_period_days: values.grace_period_days
          ? parseInt(values.grace_period_days)
          : null,
        category_id: values.category_id || null,
        provider_id: values.provider_id || null,
        allowed_terms: values.allowed_terms || null,
      },
    });
  };

  const providerOptions =
    providersData?.loanProviders?.data?.map((provider) => ({
      value: provider.id,
      label: `${provider.name} (${provider.code})`,
    })) || [];

  const categoryOptions =
    categoriesData?.loanCategories?.data?.map((category) => ({
      value: category.id,
      label: category.name,
    })) || [];

  const interestTypeOptions = [
    { value: "FLAT", label: "Flat" },
    { value: "REDUCING", label: "Reducing Balance" },
  ];

  const repaymentFrequencyOptions = [
    { value: "DAILY", label: "Daily" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "MONTHLY", label: "Monthly" },
    { value: "QUARTERLY", label: "Quarterly" },
  ];

  const processingFeeTypeOptions = [
    { value: "PERCENT", label: "Percentage" },
    { value: "FIXED", label: "Fixed Amount" },
  ];
  const { height } = useViewportSize();

  return (
    <div style={{ position: "relative", height: "75vh" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ margin: 0, marginBottom: "8px" }}>Create Loan Product</h2>
        <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
          Add a new loan product. Configure interest rates, terms, and fees. All
          fields marked with * are required.
        </p>
      </div>
      <ScrollArea
        style={{ height: height / 1.5, marginLeft: "20px" }}
        offsetScrollbars
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Grid>
            <Grid.Col span={3}>
              <TextInput
                label="Name"
                placeholder="Enter product name"
                required
                {...form.getInputProps("name")}
                mb="md"
              />
            </Grid.Col>

            {!isLoanProvider && (
              <Grid.Col span={3}>
                <Select
                  label="Loan Provider"
                  placeholder="Select provider"
                  data={providerOptions}
                  searchable
                  clearable
                  {...form.getInputProps("provider_id")}
                  mb="md"
                />
              </Grid.Col>
            )}

            <Grid.Col span={6}>
              <Textarea
                label="Description"
                placeholder="Enter description"
                minRows={2}
                {...form.getInputProps("description")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <Select
                label="Loan Category"
                placeholder="Select category"
                data={categoryOptions}
                searchable
                clearable
                {...form.getInputProps("category_id")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <Select
                label="Interest Type"
                placeholder="Select type"
                data={interestTypeOptions}
                {...form.getInputProps("interest_type")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Interest Rate (%)"
                placeholder="24.00"
                precision={2}
                min={0}
                required
                {...form.getInputProps("interest_rate")}
                mb="md"
                parser={(value) => value.replace(/[^\d.]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <Select
                label="Repayment Frequency"
                placeholder="Select frequency"
                data={repaymentFrequencyOptions}
                {...form.getInputProps("repayment_frequency")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Min Amount"
                placeholder="10000"
                min={0}
                hideControls
                required
                {...form.getInputProps("min_amount")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Max Amount"
                placeholder="500000"
                min={0}
                hideControls
                required
                {...form.getInputProps("max_amount")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Min Term (Months)"
                placeholder="1"
                min={1}
                {...form.getInputProps("min_term")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Max Term (Months)"
                placeholder="12"
                min={1}
                {...form.getInputProps("max_term")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <Select
                label="Processing Fee Type"
                placeholder="Select type"
                data={processingFeeTypeOptions}
                {...form.getInputProps("processing_fee_type")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Processing Fee"
                placeholder="2.00"
                precision={2}
                min={0}
                required
                {...form.getInputProps("processing_fee")}
                mb="md"
                parser={(value) => value.replace(/[^\d.]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Penalty Rate (%)"
                placeholder="0.20"
                precision={2}
                min={0}
                {...form.getInputProps("penalty_rate")}
                mb="md"
                parser={(value) => value.replace(/[^\d.]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Grace Period (Days)"
                placeholder="3"
                min={0}
                {...form.getInputProps("grace_period_days")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <TextInput
                label="Allowed Terms"
                placeholder="1,2,3,6,12"
                {...form.getInputProps("allowed_terms")}
                mb="md"
                description="Comma-separated list of allowed term months"
              />
            </Grid.Col>
          </Grid>

          <Group
            position="right"
            mt="xl"
            mb="xl"
            style={{ paddingRight: "20px" }}
          >
            <Button
              type="submit"
              leftIcon={<IconDeviceFloppy size={16} />}
              style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
              disabled={loading}
            >
              Save
            </Button>
          </Group>
        </form>
      </ScrollArea>
    </div>
  );
};

export default LoanProductAddModal;
